import { Field, InfoLabel, Input, InputProps, LabelProps, mergeClasses, Slot, Textarea, TextareaProps } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { ReactElement } from 'react';
import { useCommonStyles } from '../../../common/hooks';
import './FormInput.scss';

const DEFAULT_MAX_LENGTH = 524288;

interface FormInputProps {
  onChange?: (newValue: string) => void;
  label?: string;
  required?: boolean;
  validationMessage?: Slot<'div'>;
  hintMessage?: Slot<'div'>;
  tooltipText?: string;
  className?: string;
  inputProps?: Omit<InputProps, 'onChange'>;
  textAreaProps?: Omit<TextareaProps, 'onChange'>;
  descriptionContent?: ReactElement;
  inputRef?: React.Ref<HTMLInputElement>;
}

const FormInputImpl: React.FC<FormInputProps> = (props) => {
  const {
    label,
    onChange,
    required,
    validationMessage,
    hintMessage,
    tooltipText,
    className = '',
    inputProps,
    textAreaProps,
    descriptionContent,
    inputRef,
  } = props;

  const commonStyles = useCommonStyles();

  const renderTooltipLabel = (label: string, tooltipText: string, props: LabelProps) => (
    <InfoLabel
      {...props}
      label={{ className: commonStyles.inputFieldLabel }}
      info={{
        children: tooltipText,
        className: commonStyles.tooltip,
      }}
    >
      {label}
    </InfoLabel>
  );

  return (
    <Field
      className={mergeClasses('form-input', className)}
      required={required}
      validationMessage={validationMessage || null}
      validationMessageIcon={null}
      hint={hintMessage || null}
      label={label ? {
        children: tooltipText ? (_: unknown, props: LabelProps) => renderTooltipLabel(label, tooltipText, props) : label,
        className: commonStyles.inputFieldLabel,
      } : undefined}
    >
      {!!descriptionContent && <div className='form-input__description'>{descriptionContent}</div>}
      {
        !!inputProps && (
          <Input
            {...inputProps}
            ref={inputRef}
            onChange={(_, data) => onChange && onChange(data.value)}
            maxLength={inputProps?.maxLength || DEFAULT_MAX_LENGTH}
            className={inputProps.disabled ? inputProps?.className : mergeClasses(commonStyles.inputField, inputProps?.className)}
          />
        )
      }
      {
        !!textAreaProps && (
          <Textarea
            {...textAreaProps}
            onChange={(_, data) => onChange && onChange(data.value)}
            maxLength={textAreaProps?.maxLength || DEFAULT_MAX_LENGTH}
            className={textAreaProps.disabled ? textAreaProps?.className : mergeClasses(commonStyles.inputField, textAreaProps?.className)}
          />
        )
      }
    </Field >
  )
};

export const FormInput = observer(FormInputImpl);
